import { useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGenerationFormStore } from '../store'
import { uploadImage } from '../api'

export function UpscaleImageInput() {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const upscaleFactor = useGenerationFormStore((s) => s.upscaleFactor)
  const setUpscaleFactor = useGenerationFormStore((s) => s.setUpscaleFactor)
  const isGenerating = useGenerationFormStore((s) => s.isGenerating)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null)

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return
    }

    // Create local preview URL
    const localPreviewUrl = URL.createObjectURL(file)
    setPreviewUrl(localPreviewUrl)

    // Upload to ComfyUI
    setUploading(true)
    try {
      const result = await uploadImage(file)
      if (result.ok) {
        setUploadedFilename(result.value.filename)
      }
    } finally {
      setUploading(false)
    }

    setUpscaleFactor(upscaleFactor > 1 ? upscaleFactor : 2.0)

    // Clean up the previous preview URL if it exists
    return () => { URL.revokeObjectURL(localPreviewUrl) }
  }, [upscaleFactor, setUpscaleFactor])

  const handleRemoveImage = useCallback(() => {
    setUpscaleFactor(1.0)
    setPreviewUrl(null)
    setUploadedFilename(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [setUpscaleFactor])

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-tg-hint">
        {t('generate.upscale')}
      </label>

      {/* Image upload area */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isGenerating || uploading}
          className="hidden"
        />

        <button
          type="button"
          onClick={() => { fileInputRef.current?.click() }}
          disabled={isGenerating || uploading}
          className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-dashed border-tg-hint/30 px-3 py-3 text-sm font-medium transition-all duration-150 hover:border-tg-hint/50 hover:bg-tg-secondary active:scale-[0.98] disabled:opacity-50 text-tg-button"
        >
          {uploading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-tg-hint/30 border-t-tg-button-color" />
              <span>{t('generate.uploading')}</span>
            </>
          ) : (
            <>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{t('generate.uploadImage')}</span>
            </>
          )}
        </button>

        {upscaleFactor > 1 && (
          <button
            type="button"
            onClick={handleRemoveImage}
            disabled={isGenerating}
            className="rounded-md p-2 text-tg-destructive transition-all duration-150 hover:bg-tg-destructive/10 active:scale-90 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Preview */}
      {previewUrl !== null && (
        <div className="relative aspect-square w-full max-w-xs overflow-hidden rounded-lg border border-tg-hint/20">
          <img
            src={previewUrl}
            alt="Upscale source"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {/* Upscale factor slider */}
      {upscaleFactor > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-tg-hint w-16">{t('generate.upscaleFactor')}</span>
          <input
            type="range"
            min={1.5}
            max={4}
            step={0.5}
            value={upscaleFactor}
            onChange={(e) => { setUpscaleFactor(Number(e.target.value)) }}
            disabled={isGenerating}
            className="flex-1 accent-tg-button-color"
          />
          <span className="text-xs text-tg-text w-10 text-right tabular-nums">
            {upscaleFactor.toFixed(1)}x
          </span>
        </div>
      )}
    </div>
  )
}
